import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { IconButton, Tooltip } from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { Links } from 'containers/AppLayout/menu/linkBuilder';
import TuneRoundedIcon from '@material-ui/icons/TuneRounded';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import styles from './QueryDetailLink.module.scss';

export const buildQueryDetailTarget = (shopId, query, returnTo) => {
  const params = new URLSearchParams({ return_to: returnTo });
  return `${Links.searchQueryDetail(shopId, query)}?${params.toString()}`;
};

const useQueryDetailTarget = (shopId, query, enabled) => {
  const location = useLocation();
  if (!enabled) return '';
  return buildQueryDetailTarget(
    shopId,
    query,
    `${location.pathname}${location.search}`,
  );
};

/** Product-neutral query cell gated by the query-detail transport capability. */
const QueryDetailLink = ({ shopId, query, enabled, activeRuleCount }) => {
  const t = useLocalizedMessage();
  const target = useQueryDetailTarget(shopId, query, enabled);
  if (!enabled) return query;

  return (
    <Link
      aria-label={t('analytics.query.openDetails', { query })}
      className={styles.link}
      title={t('analytics.query.openDetails', { query })}
      to={target}
    >
      <span className={styles.queryText}>{query}</span>
      {activeRuleCount > 0 && (
        <span
          className={styles.ruleIndicator}
          title={t('analytics.query.rules.active', { count: activeRuleCount })}
        >
          <TuneRoundedIcon aria-hidden="true" />
        </span>
      )}
    </Link>
  );
};

/** Compact secondary entry point for dense report tables. */
export const QueryDetailEditAction = ({ shopId, query, enabled }) => {
  const t = useLocalizedMessage();
  const target = useQueryDetailTarget(shopId, query, enabled);
  if (!enabled) return null;

  return (
    <Tooltip title={t('button.modify')}>
      <IconButton
        aria-label={t('analytics.query.openDetails', { query })}
        className={styles.editAction}
        component={Link}
        role="link"
        size="small"
        to={target}
      >
        <EditOutlinedIcon aria-hidden="true" fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

QueryDetailLink.propTypes = {
  shopId: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  activeRuleCount: PropTypes.number,
};

QueryDetailLink.defaultProps = {
  enabled: false,
  activeRuleCount: 0,
};

QueryDetailEditAction.propTypes = {
  shopId: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
};

QueryDetailEditAction.defaultProps = {
  enabled: false,
};

export default QueryDetailLink;
